import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getToken } from '../../utils/auth';
import { getApiEndpoint } from '../../config/api';

const ProtectedRoute = ({ children, allowedRoles = [], redirectTo = '/login' }) => {
  const token = getToken();
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  const [loading, setLoading] = useState(!userRole && token);
  /** null = not needed; true/false = profile allowlist for Cloud Staff routes */
  const [fullCloudStaffPortalAccess, setFullCloudStaffPortalAccess] = useState(null);
  
  // Fetch user role from token or API if not in localStorage
  useEffect(() => {
    if (!userRole && token) {
      // Try to decode role from JWT token first (synchronous, no API call needed)
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const role = payload.role;
          if (role) {
            console.log('Extracted role from JWT token:', role);
            localStorage.setItem('userRole', role);
            if (payload.isCompanyAdmin) localStorage.setItem('isCompanyAdmin', 'true');
            if (payload.accountType) localStorage.setItem('accountType', payload.accountType);
            setUserRole(role);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        console.error('Error decoding token:', e);
      }
      
      // Fallback: fetch from API - try profile first, then RBAC roles
      const fetchUserRole = async () => {
        try {
          // First try profile endpoint
          let response = await fetch(getApiEndpoint('/api/users/profile'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            const role = userData.role;
            if (role) {
              console.log('Fetched role from profile API:', role);
              localStorage.setItem('userRole', role);
              if (userData.isCompanyAdmin) localStorage.setItem('isCompanyAdmin', 'true');
              else localStorage.removeItem('isCompanyAdmin');
              if (userData.accountType) localStorage.setItem('accountType', userData.accountType);
              setUserRole(role);
              setLoading(false);
              return;
            }
          }
          
          // If profile doesn't have role, try RBAC roles endpoint
          // First get user ID from token
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const userId = payload.userId || payload.id;
            
            if (userId) {
              response = await fetch(getApiEndpoint(`/api/rbac/users/${userId}/roles`), {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const roles = await response.json();
                if (roles && roles.length > 0) {
                  // Get the highest level role (should be first due to ORDER BY level DESC)
                  const primaryRole = roles[0].role_name || roles[0].name;
                  if (primaryRole) {
                    console.log('Fetched role from RBAC API:', primaryRole);
                    localStorage.setItem('userRole', primaryRole);
                    setUserRole(primaryRole);
                    setLoading(false);
                    return;
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchUserRole();
    } else if (userRole) {
      setLoading(false);
    } else if (!token) {
      setLoading(false);
    }
  }, [token]); // Only depend on token, not userRole, to avoid infinite loops

  // Normalize role comparison (handle case and whitespace)
  const normalizedUserRole = userRole ? userRole.trim().toLowerCase() : null;
  const normalizedAllowed = allowedRoles.map(role => role ? role.trim().toLowerCase() : '');

  // Treat super_admin as admin if admin is allowed
  const effectiveAllowed = normalizedAllowed.includes('admin') && !normalizedAllowed.includes('super_admin')
    ? [...normalizedAllowed, 'super_admin']
    : normalizedAllowed;

  const roleAllowed = normalizedUserRole && effectiveAllowed.includes(normalizedUserRole);
  const needsCloudStaffEmailBypass =
    allowedRoles.length > 0 &&
    allowedRoles.some((r) => (r || '').trim().toLowerCase() === 'cloud_staff') &&
    normalizedUserRole &&
    !roleAllowed;

  useEffect(() => {
    if (!needsCloudStaffEmailBypass || !token) {
      setFullCloudStaffPortalAccess(null);
      return undefined;
    }
    let cancelled = false;
    setFullCloudStaffPortalAccess(null);
    (async () => {
      try {
        const res = await fetch(getApiEndpoint('/api/users/profile'), {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
          if (!cancelled) setFullCloudStaffPortalAccess(false);
          return;
        }
        if (cancelled) return;
        const d = await res.json();
        if (!cancelled) setFullCloudStaffPortalAccess(!!d.hasFullCloudStaffPropertyAccess);
      } catch {
        if (!cancelled) setFullCloudStaffPortalAccess(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsCloudStaffEmailBypass, token]);
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Wait for role to be loaded before checking permissions
  if (loading) {
    return <div>Loading...</div>;
  }
  
  // If no specific roles are required, just check if user is authenticated
  if (allowedRoles.length === 0) {
    return children;
  }

  if (needsCloudStaffEmailBypass && fullCloudStaffPortalAccess === null) {
    return <div>Loading...</div>;
  }

  if (needsCloudStaffEmailBypass && fullCloudStaffPortalAccess === true) {
    return children;
  }

  // Check if user's role is in the allowed roles
  if (!normalizedUserRole || !effectiveAllowed.includes(normalizedUserRole)) {
    console.warn('ProtectedRoute: Access denied', {
      userRole: normalizedUserRole,
      allowedRoles: effectiveAllowed,
      path: window.location.pathname
    });
    
    // Redirect based on user role
    switch (normalizedUserRole) {
      case 'super_admin':
      case 'admin':
        return <Navigate to="/dashboard/admin-panel" replace />;
      case 'contractor':
        if (localStorage.getItem('isCompanyAdmin') === 'true') {
          return <Navigate to="/contractor/dashboard" replace />;
        }
        return <Navigate to="/contractor/staff-dashboard" replace />;
      case 'cloud_staff':
        return <Navigate to="/cloud-staff/dashboard" replace />;
      case 'building_manager':
        return <Navigate to="/building-manager/dashboard" replace />;
      case 'staff':
        return <Navigate to="/staff/dashboard" replace />;
      case 'fm':
        return <Navigate to="/fm/dashboard" replace />;
      case 'property-onboarding-staff':
        return <Navigate to="/dashboard/admin-panel" replace />;
      case 'demo':
        return <Navigate to="/dashboard" replace />;
      case 'viewer':
      default:
        return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute; 